import { Box } from '@rebass/grid';
import { useRouter } from 'next/router';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import getUnitTypeConsts from '../constants/getUnitTypeConsts';
import {
    LeaveReviewInput,
    LeaveUnauthenticatedReviewInput,
    OrderItemFragment,
    OrderItemFragmentDoc,
    RentalUnitReviewFragment,
    RentalUnitTypeEnum,
    ReviewModal_Booking_Fragment,
    useLeaveReviewMutation,
    useLeaveUnauthenticatedReviewMutation,
} from '../generated/graphql';
import Divider from './Divider';
import { Handler } from './Formed';
import BookingReviewForm, { Values } from './BookingReviewForm';
import useGetPageSlugByLocale from '../hooks/useGetPageSlugByLocale';
import ReviewDone from './ReviewDone';
import LeaveReview from './LeaveReview';

interface Props {
    booking?: ReviewModal_Booking_Fragment;
    setLeftReview: (val: boolean | null) => void;
    leftReview: boolean | null;
    rentalUnit?: RentalUnitReviewFragment | null;
    isAnonymous?: boolean;
    loading?: boolean;
}

const LeaveBookingReview: FC<React.PropsWithChildren<Props>> = ({
    booking,
    leftReview: isReviewDone,
    setLeftReview,
    rentalUnit,
    isAnonymous,
    loading,
}) => {
    const [mutate] = useLeaveReviewMutation();
    const [unauthenticatedMutate] = useLeaveUnauthenticatedReviewMutation();
    const { getPageSlugByLocale } = useGetPageSlugByLocale();

    const accommodationPageSlug = getPageSlugByLocale('ACCOMMODATION');
    const rentalUnitType = booking?.rentalUnit?.type ?? rentalUnit?.type;

    const isCamping =
        rentalUnitType === RentalUnitTypeEnum.CamperSpot || rentalUnitType === RentalUnitTypeEnum.CampingPitch;

    const intl = useIntl();
    const {
        query: { bookingHash },
        locale,
    } = useRouter();
    const handleSubmit: Handler<Values> = async values => {
        const ratings = {
            ratingLocation: Number(values.ratingLocation),
            ratingHospitality: Number(values.ratingHospitality),
            ratingLayout: Number(values.ratingLayout),
            ratingMaintenance: Number(values.ratingMaintenance),
            ratingValueForMoney: Number(values.ratingValueForMoney),
        };
        if (isAnonymous) {
            if (!bookingHash) {
                return;
            }
            await unauthenticatedMutate({
                variables: {
                    input: {
                        bookingHash: bookingHash as string,
                        ...values,
                        ...ratings,
                    } as LeaveUnauthenticatedReviewInput,
                },
            });
            setLeftReview(true);
            return;
        }

        if (!booking?.id) {
            return;
        }
        await mutate({
            variables: {
                input: {
                    bookingId: booking.id,
                    ...values,
                    ...ratings,
                    // We know it is, because of Yup
                } as LeaveReviewInput,
            },
            update: (cache, { data: payload }) => {
                const opts = {
                    id: `${booking?.__typename ?? 'Booking'}:${booking.id}`,
                    fragment: OrderItemFragmentDoc,
                    fragmentName: 'OrderItem',
                };
                const old = cache?.readFragment<OrderItemFragment>(opts);

                if (!old || !payload?.leaveReview.review) {
                    return;
                }

                cache.writeFragment({
                    ...opts,
                    data: { ...old, review: payload?.leaveReview.review },
                });
            },
        });
        setLeftReview(true);
    };

    if (isReviewDone) {
        return (
            <ReviewDone
                orderItem={booking}
                setLeftReview={setLeftReview}
                isAnonymous={isAnonymous}
                pushUrl="/[accommodation]/[slug]"
                pathName={`/${accommodationPageSlug}/${rentalUnit?.slug}`}
                locale={locale}
            />
        );
    }

    return (
        <>
            {rentalUnit && (
                <LeaveReview
                    item={booking}
                    object={rentalUnit}
                    webpThumbnail={rentalUnit?.webpThumbnail}
                    jpegThumbnail={rentalUnit?.jpegThumbnail}
                    locale={locale}
                    isAnonymous={isAnonymous}
                    loading={loading}
                    pushUrl={`/${accommodationPageSlug}/${rentalUnit?.slug}`}
                    strapline={(rentalUnit?.accommodation.hasPublicPage && rentalUnit?.accommodation.name) || undefined}
                    subtitle={getUnitTypeConsts(intl)[rentalUnit.type].translation}
                />
            )}
            <Box order={2} width={['100%', null, '70%', '60%']}>
                <Divider mt={0} mb={8} />
                <BookingReviewForm handleSubmit={handleSubmit} isCamping={isCamping} />
            </Box>
        </>
    );
};

export default LeaveBookingReview;
